import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AccessCondition, AccessString, AuthService } from 'providers/auth.service';
import { ConfigService } from 'providers/config.service';

export interface BetaLinkState {
    skipDialog: boolean;
    betaLink: string;
}

@Injectable({
    providedIn: 'root',
})
export class BetaLinkService {
    private readonly STORAGE_KEY = 'hide-beta-update-dialog';
    private readonly baseUrl: string;
    public betaLink: string;
    /** CE beta Support portal page */
    public supportPortalLink: string;

    /**
     * Whether to offer the CE beta Support portal. The permission is granted by the
     * `support-portal` feature license, or by an internal role.
     */
    showBetaSupportPortal$: Observable<boolean> = this.hasAccess$('supportPortal.access');

    constructor(
        private readonly config: ConfigService,
        private readonly dialog: MatDialog,
        private readonly auth: AuthService
    ) {
        this.baseUrl = this.config?.config.apiBaseUrl || '';
        this.betaLink = `https://${this.baseUrl}/2.0/?=beta:true`;
        this.supportPortalLink = `https://${this.baseUrl}/2.0/help/support`;
    }

    private hasAccess$(accessString: AccessString, condition?: AccessCondition): Observable<boolean> {
        return this.auth.permissions$.pipe(
            map((permissions) => permissions.hasAccess(accessString, condition)),
            distinctUntilChanged()
        );
    }

    /**
     * Get beta link state as an observable
     * The link is offered on every origin and license tier, so this only carries the URL and
     * whether the notification dialog was dismissed for good
     */
    getBetaLinkState$(): Observable<BetaLinkState> {
        return of({
            skipDialog: this.getSkipDialogPreference(),
            betaLink: this.betaLink,
        });
    }

    /**
     * Get the skip dialog preference from localStorage
     */
    getSkipDialogPreference(): boolean {
        return localStorage.getItem(this.STORAGE_KEY) === 'true';
    }

    /**
     * Set the skip dialog preference in localStorage
     */
    setSkipDialogPreference(skip: boolean): void {
        localStorage.setItem(this.STORAGE_KEY, skip.toString());
    }

    /**
     * Navigate to the beta version
     * @param redirect - If true, immediately redirect. If false, show dialog first.
     * @param dialogComponent - The dialog component to open (optional, for avoiding circular deps)
     * @param destinationUrl - Optional custom URL to navigate to instead of the default beta link
     */
    navigateToBeta(redirect?: boolean, dialogComponent?: any, destinationUrl?: string): void {
        const url = destinationUrl ?? this.betaLink;
        if (redirect) {
            globalThis.location.href = url;
            return;
        }
        this.openDialog(dialogComponent, url);
    }

    /**
     * Open the beta notification dialog
     * @param dialogComponent - The dialog component class to open
     * @param destinationUrl - URL to navigate to when user confirms
     */
    openDialog(dialogComponent?: any, destinationUrl?: string): void {
        const url = destinationUrl ?? this.betaLink;
        const skipDialog = this.getSkipDialogPreference();
        if (skipDialog) {
            globalThis.location.href = url;
            return;
        }

        if (!dialogComponent) {
            console.error('Dialog component not provided to BetaLinkService.openDialog');
            return;
        }

        const dialogRef = this.dialog.open(dialogComponent);

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.doNotShow) {
                this.setSkipDialogPreference(true);
            }
            if (result?.gotoBeta) {
                globalThis.location.href = url;
            }
        });
    }
}
